import * as React from "react";
import { useState } from "react";
import { Button } from "../ui/button";
import { BiPlus } from "react-icons/bi";
import { FaEdit } from "react-icons/fa";
import { toaster } from "../ui/toaster";
import { Checkbox } from "../ui/checkbox";
import MDEditor from '@uiw/react-md-editor';
import { useColorMode } from "../ui/color-mode";
import { HiCheck, HiPlus } from "react-icons/hi";
import { IAnnouncement } from "@/pages/announcements";
import { useWebHelper } from "@viabl/echo-web-client-core";
import { FloatingLabelInput } from "../ui/floating-input-label";
import { Dialog, Field, Fieldset, Flex, Separator, Tag, Text, useBreakpointValue, useDisclosure } from '@chakra-ui/react';
import { DialogBody, DialogContent, DialogFooter, DialogRoot, DialogTitle, DialogTrigger } from "../ui/dialog";

interface newUserProps {
    cb: Function,
    existingAnnouncement: IAnnouncement | null,
    intent: "edit" | "create", //lets us use this component for both editing and creating announcements, just need to change some of the text and the api call
    disabled?: boolean
}

interface IDiscipline {
    id: string,
    name: string,
    skill_reference_id: string,
    target_type: string,
    accent: string,
}

const colorMap = new Map([
    ["#9ACD32", "green"],
    ["#A0522D", "orange"],
    ["#1E90FF", "blue"],
    ["#9370DB", "purple"],
    ["#DC143C", "red"],
    ["#FF69B4", "pink"],
    ["#FFD700", "yellow"],
])

export function getColorName(hex: string) {
    return colorMap.get(hex.toUpperCase()) || "gray"
}

export default function AnnouncementEditor(props: newUserProps) {
    const { onOpen, onClose } = useDisclosure();
    const [disciplines, setDisciplines] = useState<IDiscipline[]>([])
    const [targetType, setTargetType] = useState<string>("")
    const [id, setId] = useState<string>()
    const [title, setTitle] = useState<string>("");
    const [subtitle, setSubtitle] = useState<string>("");
    const [body, setBody] = useState<string>("");
    const [tags, setTags] = useState<string[]>([]);
    const [isAcceptable, setIsAcceptable] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const [value, setValue] = useState<string | undefined>("**Hello world!!!**");
    const { colorMode } = useColorMode();
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const webHelpers = useWebHelper();

    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {}

        if (!title.trim()) {
            newErrors.title = "Title is required"
        }
        if (!value || !value.trim()) {
            newErrors.body = "Body/Content is required"
        }
        if (!targetType) {
            newErrors.targetType = "Target type is required"
        }
        if (tags.length === 0) {
            newErrors.tags = "At least one target group is required"
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleClickOpen = () => {

        onOpen();
        setOpen(true);
        setErrors({})

        webHelpers.GetAsync("/api/targetgroup/all", "iris-api").then((data: any) => {
            setDisciplines(data)
        })

        if (props.intent === "edit" && props.existingAnnouncement != null) {
            setId(props.existingAnnouncement.id)
            setTitle(props.existingAnnouncement.title);
            setTargetType(props.existingAnnouncement.target_type)
            setSubtitle(props.existingAnnouncement.subtitle ? props.existingAnnouncement.subtitle : "");
            setValue(props.existingAnnouncement.body);
            setTags(props.existingAnnouncement.target_groups);
            setIsAcceptable(props.existingAnnouncement.is_acceptable);
        }
        else {
            setTitle("");
            setTargetType("")
            setSubtitle("");
            setValue("");
            setTags([]);
            setIsAcceptable(false);
        }

    }

    const handleCreate = () => {
        if (!validateForm()) {
            toaster.create({
                title: "Validation Error",
                description: "Please fill in all required fields",
                type: "error",
            })
            return
        }

        let announcement = {
            title: title,
            subtitle: subtitle,
            body: value,
            target_type: targetType,
            target_groups: tags,
            is_acceptable: isAcceptable,
        }

        webHelpers.PostAsync("/api/announcement/create", "iris-api", announcement).then((data: any) => {
            handleClose()
            props.cb()

            toaster.create({
                title: `Announcement has been created`,
                type: "success",
            })
        })
    }

    const handleEdit = () => {
        if (!validateForm()) {
            toaster.create({
                title: "Validation Error",
                description: "Please fill in all required fields",
                type: "error",
            })
            return
        }

        let announcement = {
            title: title,
            subtitle: subtitle,
            body: value,
            target_type: targetType,
            target_groups: tags,
            is_acceptable: isAcceptable,
        }

        webHelpers.PutAsync("/api/announcement/" + id + "/update", "iris-api", announcement).then((data: any) => {
            handleClose()
            props.cb()

            toaster.create({
                title: `Announcement has been edited`,
                type: "success",
            })
        })
    }

    const handleClose = () => {
        setOpen(false);
        onClose();
    }

    const handleTitleChange = (e: React.FormEvent<HTMLInputElement>) => {
        setTitle(e.currentTarget.value);
    }
    const handleSubtitleChange = (e: React.FormEvent<HTMLInputElement>) => {
        setSubtitle(e.currentTarget.value);
    }
    const handleBodyChange = (e: React.FormEvent<HTMLInputElement>) => {
        setBody(e.currentTarget.value);
    }
    const handleTagsChange = (e: React.FormEvent<HTMLInputElement>) => {
        setTags([e.currentTarget.value]);
    }
    const handleAcceptableChange = () => setIsAcceptable(!isAcceptable);

    const showText = useBreakpointValue({
        base: false,
        lg: true,
    })

    return (
        <>
            <DialogRoot
                open={open}
                aria-describedby="alert-dialog-slide-description"
                size={{ base: "md", lg: "full"}}
            >
                <Dialog.Backdrop />
                <DialogTrigger>



                    <Button bg={"AbleBlue"} color={"white"} size={"sm"} marginLeft={{ base: "10px", lg: "30px" }}
                        disabled={props.disabled}
                        _hover={{ bg: "AbleYellow", color: "white" }}
                        _active={{ bg: "#ECB500" }}
                        onClick={handleClickOpen}>
                        {props.intent === "create" ? <BiPlus /> : <FaEdit />}
                        {showText ? (props.intent === "create"? "Create Announcement": "Edit") : (props.intent === "create"? "": "Edit")}
                    </Button>
                </DialogTrigger>
                <DialogContent marginTop={"0px"} marginBottom={"0px"}>
                    <DialogTitle>{props.intent === "create" ? "Create New Announcement" : "Edit Announcement"}</DialogTitle>
                    <DialogBody padding={"10px"}>
                        <Fieldset.Root>
                            <Field.Root marginTop={"3px"}>
                                <FloatingLabelInput label="Title" value={title} onChange={handleTitleChange} />
                                {errors.title && (
                                    <Text color="red.500" fontSize="sm" marginTop="4px">
                                        {errors.title}
                                    </Text>
                                )}
                            </Field.Root>
                            <Field.Root>
                                <FloatingLabelInput label="Subtitle" value={subtitle} onChange={handleSubtitleChange} />
                            </Field.Root>
                            <Separator marginY={"10px"} />
                            <Flex overflowX={"auto"} marginTop={"-5px"} direction={"row"} flexWrap={"wrap"}>
                                {disciplines?.map((discipline) => (
                                    <Tag.Root
                                        key={discipline.id}
                                        variant={tags.includes(discipline.id) ? "solid" : "surface"}
                                        size={"lg"}
                                        margin={"5px"}
                                        opacity={targetType !== "" && discipline.target_type !== targetType ? 0.5 : 1}
                                        colorPalette={getColorName(discipline.accent)}
                                        _hover={{
                                            cursor:
                                                targetType !== "" && discipline.target_type !== targetType
                                                    ? "not-allowed"
                                                    : "pointer",
                                            brightness:
                                                targetType !== "" && discipline.target_type !== targetType
                                                    ? "100%"
                                                    : "120%",
                                            color: "primaryTextInverted",
                                            borderWidth: "2px",
                                            borderStyle: "solid",
                                            borderColor: discipline.accent,
                                            marginLeft: "3px",
                                            marginRight: "3px",
                                        }}
                                        onClick={() => {
                                            const isDisabled =
                                                targetType !== "" && discipline.target_type !== targetType

                                            if (isDisabled) return

                                            if (!tags.includes(discipline.id)) {
                                                const newTags = [...tags, discipline.id]
                                                setTags(newTags)

                                                if (tags.length === 0) {
                                                    setTargetType(discipline.target_type.toString())
                                                }
                                            } else {
                                                const newTags = tags.filter((tag) => tag !== discipline.id)
                                                setTags(newTags)

                                                if (newTags.length === 0) {
                                                    setTargetType("")
                                                }
                                            }
                                        }}
                                    >
                                        <Tag.StartElement>
                                            {tags.includes(discipline.name) ? <HiCheck /> : <HiPlus />}
                                        </Tag.StartElement>

                                        <Tag.Label>{discipline.name}</Tag.Label>
                                    </Tag.Root>
                                ))}
                            </Flex>
                            {errors.tags && (
                                <Text color="red.500" fontSize="sm" marginTop="4px">
                                    {errors.tags}
                                </Text>
                            )}
                            {/* need to validate above field, just not sure how to do it in chakra v3 "onInvalid={!isValidEmail}" */}
                            {/* autocomplete is janky, take a look at it */}
                            {/*BODY goes here, needs to be a markdown editor */}
                            <MDEditor
                                value={value}
                                onChange={setValue}

                                data-color-mode={colorMode === "light" ? "light" : "dark"}
                            />

                            {errors.body && (
                                <Text color="red.500" fontSize="sm" marginTop="4px">
                                    {errors.body}
                                </Text>
                            )}

                            {/*TAGS goes here, needs to be a tag input, im thinking maybe using Chakra Tags for this */}
                            <Checkbox marginTop={"10px"} marginLeft={"3px"} checked={isAcceptable} onChange={handleAcceptableChange}>Is Acceptable?</Checkbox>
                        </Fieldset.Root>
                    </DialogBody>
                    <DialogFooter>
                        <Button onClick={props.intent === "edit" ? handleEdit : handleCreate}
                            disabled={false}
                            bg={"AbleBlue"}
                            color={"white"}
                            _hover={{ color: "AbleYellow" }}>{props.intent === "edit" ? "Edit Announcement" : "Create Announcement"}</Button>
                        <DialogTrigger>
                            <Button onClick={handleClose} colorScheme="red" marginLeft={"10px"}>Cancel</Button>
                        </DialogTrigger>
                    </DialogFooter>
                </DialogContent>
            </DialogRoot>
        </>
    );
}
